import type { OrganizationBillingMethod } from "@arbeidsark/shared"
import { useCallback, useRef } from "react"
import styled from "styled-components"
import { FormField, Input, Label } from "~/components"
import Radio from "~/components/Radio/Radio"
import RadioCheck from "~/components/Radio/RadioCheck"
import { useClickOutside, useForm, useTranslate } from "~/ur-react-hooks"
import { useToast } from "~/utils"
import { useErrorHandling } from "~/utils/error"
import { useOrganizationMembership } from "~/utils/hooks/useOrganizationMembership"
import { trpc } from "~/utils/trpc"

const Layout = styled.div``

const Title = styled.h4``

const PostInfoFormFields = styled.div`
  display: grid;

  grid-template-columns: 1fr 2fr;
  gap: 0.625rem;
`

const BillingMethods = styled.div`
  display: flex;
  flex-direction: column;

  gap: 0.625rem;
`

const BillingMethodContentTitle = styled.h5`
  margin: 0;
  display: inline-block;
`

const BillingMethodContentDescription = styled.span`
  font-size: 0.875rem;
  margin-left: 1rem;
  word-break: break-word;
`

export type SettingsAdminBillingProps = {}

interface BillingForm {
  name: string
  organizationNumber: string | null
  address: string | null
  postCode: string | null
  postalCity: string | null
  billingMethod: OrganizationBillingMethod
  billingEmail: string | null
}

const SettingsAdminBilling: React.FC<SettingsAdminBillingProps> = ({}) => {
  const organizationMembership = useOrganizationMembership()!
  const organization = organizationMembership.organization

  const translations = useTranslate({
    title: "settings.billing.title",
    school: "common.school",
    organizationNumber: "common.organization-number",
    address: "common.address",
    postCode: "common.post-code",
    city: "common.city",
    billingMethod: "settings.labels.billing-method",
    ehf: "billing-methods.ehf",
    ehfLabel: "billing-methods.labels.ehf",
    email: "common.email",
    billingEmail: "settings.billing.billing-email",
    savedSuccesfully: "settings.saved-successfully",
  })

  const {
    formValues,
    formEdited,
    formValid,
    formChangeHandler,
    updateForm,
    updateInitialValues,
    resetForm,
  } = useForm<BillingForm>({
    values: {
      name: organization.name,
      organizationNumber: organization.organizationNumber,
      address: organization.address,
      postCode: organization.postCode,
      postalCity: organization.postalCity,
      billingMethod: organization.billingMethod,
      billingEmail: organization.billingEmail,
    },
  })

  const trpcContext = trpc.useContext()

  const { getToastInfo } = useErrorHandling()
  const toast = useToast()

  const { mutate: updateOrganization } = trpc.organizations.update.useMutation({
    onSuccess() {
      toast("success", translations.savedSuccesfully)
      trpcContext.users.me.invalidate()
    },
    onError(error) {
      const { type, message } = getToastInfo(
        error.message,
        error.data?.code,
        "error",
      )

      toast(type, message)
    },
  })

  const save = useCallback(() => {
    if (!formEdited || !formValid) {
      return
    }

    updateOrganization({
      id: organization.id,
      data: {
        ...formValues,
      },
    })

    updateInitialValues({
      ...formValues,
    })

    resetForm()
  }, [
    formEdited,
    formValid,
    updateOrganization,
    updateInitialValues,
    organization,
    formValues,
    resetForm,
  ])

  const ref = useRef<HTMLDivElement>(null)

  useClickOutside(ref, () => {
    save()
  })

  return (
    <Layout ref={ref}>
      <Title>{translations.title}</Title>
      <FormField>
        <Label>{translations.school}</Label>
        <Input
          value={formValues.name}
          onChange={formChangeHandler("name")}
          width="100%"
        />
      </FormField>
      <FormField>
        <Label>{translations.organizationNumber}</Label>
        <Input
          value={formValues.organizationNumber ?? ""}
          onChange={formChangeHandler("organizationNumber")}
          width="100%"
        />
      </FormField>
      <FormField>
        <Label>{translations.address}</Label>
        <Input
          value={formValues.address ?? ""}
          onChange={formChangeHandler("address")}
          width="100%"
        />
      </FormField>
      <PostInfoFormFields>
        <FormField>
          <Label>{translations.postCode}</Label>
          <Input
            value={formValues.postCode ?? ""}
            onChange={formChangeHandler("postCode")}
            width="100%"
          />
        </FormField>
        <FormField>
          <Label>{translations.city}</Label>
          <Input
            value={formValues.postalCity ?? ""}
            onChange={formChangeHandler("postalCity")}
            width="100%"
          />
        </FormField>
      </PostInfoFormFields>
      {formValues.billingMethod === "EMAIL" && (
        <FormField>
          <Label>{translations.billingEmail}</Label>
          <Input
            value={formValues.billingEmail ?? ""}
            onChange={formChangeHandler("billingEmail")}
            width="100%"
          />
        </FormField>
      )}
      <Label>{translations.billingMethod}</Label>
      <BillingMethods>
        <Radio
          checked={formValues.billingMethod === "EHF"}
          onChange={() => {
            updateForm({ billingMethod: "EHF" })
          }}
        >
          <BillingMethodContentTitle>
            {translations.ehf}
          </BillingMethodContentTitle>
          <BillingMethodContentDescription>
            {translations.ehfLabel}
          </BillingMethodContentDescription>
        </Radio>
        <Radio
          checked={formValues.billingMethod === "EMAIL"}
          onChange={() => {
            updateForm({ billingMethod: "EMAIL" })
          }}
        >
          <BillingMethodContentTitle>
            {translations.email}
          </BillingMethodContentTitle>
          <BillingMethodContentDescription>
            {formValues.billingEmail}
          </BillingMethodContentDescription>
        </Radio>
      </BillingMethods>
    </Layout>
  )
}

export default SettingsAdminBilling
